import { Div, FormStatus, InfoCard, ProgressIndicator } from '@dnb/eufemia';
import { useMemo } from 'react';

import { useDebounce } from '@/hooks/useDebounce';
import { ApiAppClientResponse } from '@/openapi/schema';

import ClientTile from '../ClientTile';
import { ViewType } from '../schemas';

export default function ClientView({
  consumerClients,
  isLoading,
  showView,
  query,
}: {
  consumerClients: ApiAppClientResponse[] | undefined;
  isLoading: boolean;
  showView: (view: ViewType, query?: string) => void;
  query: string;
}) {
  const debouncedQuery = useDebounce(query, 300);

  const filteredConsumerClients = useMemo(() => {
    if (!consumerClients) return [];
    const lowerCaseQuery = debouncedQuery.toLowerCase();
    return consumerClients
      .filter((client) => {
        const clientName = client.name?.toLowerCase() || '';
        const clientId = client.ciamClientId?.toLowerCase() || '';
        const clientDescription = client.description?.toLowerCase() || '';
        const appId = client.appId?.toLowerCase() || '';
        const liveMode = client.liveMode ? 'live' : 'test';
        const scopes = client.scopes ? client.scopes.map((scope) => scope.toLowerCase()) : [];
        const ciamApplicationId = client.ciamAppId?.toLowerCase() || '';

        return (
          clientName.includes(lowerCaseQuery) ||
          clientId.includes(lowerCaseQuery) ||
          clientDescription.includes(lowerCaseQuery) ||
          appId.includes(lowerCaseQuery) ||
          liveMode.includes(lowerCaseQuery) ||
          scopes.some((scope) => scope.includes(lowerCaseQuery)) ||
          ciamApplicationId.includes(lowerCaseQuery)
        );
      })
      .map((client) => (
        <Div bottom="small" key={client.ciamClientId}>
          <ClientTile client={client} showView={showView} />
        </Div>
      ));
  }, [consumerClients, debouncedQuery, showView]);

  if (consumerClients && !isLoading && consumerClients.length === 0) {
    return <InfoCard stretch text="Currently no clients have been granted access to this integration service." />;
  }

  return (
    <>
      <FormStatus bottom="small" state="warning" stretch>
        Only clients onboarded in the developer portal are displayed.
      </FormStatus>
      {isLoading && <ProgressIndicator size="small" type="linear" />}
      {filteredConsumerClients.length > 0 ? (
        filteredConsumerClients
      ) : (
        <InfoCard stretch text="No clients match the current filters." />
      )}
    </>
  );
}
